import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GeoJSONLayer } from './components/GeoJSONImporter';
import { usePlanNodes, usePlanLink, useStreetLight } from '../../planBT/hooks';
import { syncMinigridMeters } from '../../planBT/services/planAPI';
import { calculateFitMapSettings, calculateMapBounds, calculateGpsBounds } from './utils/presenter.utils';
import { GridEditorState, GridEditorActions, GpsBounds, LayerVisibility, MetricsVisibility } from './types/presenter.types';
import { useDataManagement } from './hooks/useDataManagement';
import { useEventHandlers } from './hooks/useEventHandlers';
import { useElementManagement } from './hooks/useElementManagement';
import { ElementAction, GridElement, ChangesSummaryPlanBT, Connection, Light } from './types';
import useGridEditorForPlanBT from './hooks/useGridEditorForPlanBT';
import { mergeElementsWithSameCoordinates } from './utils/elementMerger';
import { useAppRole } from '../../rbac';


// Re-export types for external use
export type { GridEditorState, GridEditorActions, GpsBounds, LayerVisibility } from './types/presenter.types';

/**
 * Main presenter hook for the Grid Editor component
 * Manages all state and provides actions for grid editing functionality
 * @param svgRef - Reference to the SVG canvas element for coordinate calculations
 * @returns Object containing state, actions, and configuration data
 */
export const useGridEditorPresenter = (svgRef: React.RefObject<SVGSVGElement>) => {
  // Extract planBTId from URL parameters to load the correct plan
  const params = useParams<{ planBTId: string }>();
  const planBTId = params.planBTId;
  const [searchParams] = useSearchParams();

  // Fetch plan data from API using custom hooks
  const { getAllNodesQuery } = usePlanNodes(planBTId);
  const { allLinkQuery } = usePlanLink(planBTId);
  const { getAllStreetLightsQuery } = useStreetLight(planBTId);

  // RBAC : l'auto-synchro exige planbt.sync (accordé au serveur, viewer+) ; l'édition planbt.edit.
  // Les deux valent false tant que le rôle n'est pas connu (fail-closed) : la
  // synchro attend donc la résolution du rôle avant de partir.
  const { capabilities, isResolved: isRoleResolved } = useAppRole();
  const canSyncMeters = capabilities.has('planbt.sync');
  const canEditPlan = capabilities.has('planbt.edit');
  // Retrait effectif du droit : rôle connu ET planbt.edit absent. Un rôle
  // inconnu (chargement, erreur sans donnée récente) ne vaut pas retrait.
  const editRightsRevoked = isRoleResolved && !canEditPlan;

  // Auto-inscription des compteurs localises du minigrid relie, a l'ouverture du plan.
  // Une seule fois par planBTId. Backend idempotent (ne recree pas un compteur deja
  // present) et sans verrou geoloc "manuel". Un echec n'empeche pas l'ouverture du plan.
  const syncedPlanRef = useRef<string | null>(null);
  useEffect(() => {
    if (!planBTId || !canSyncMeters || syncedPlanRef.current === planBTId) {
      return;
    }
    syncedPlanRef.current = planBTId;
    syncMinigridMeters(planBTId)
      .then(() => {
        // La synchro peut creer des liens compteur->poteau SANS ajouter de noeud
        // (added=0 quand les compteurs existent deja comme noeuds). On rafraichit
        // donc systematiquement noeuds ET liens, sinon les liaisons creees cote
        // backend n'apparaissent pas tant que la page n'est pas rechargee.
        getAllNodesQuery?.refetch?.();
        allLinkQuery?.refetch?.();
      })
      .catch((e) => {
        // Pas l'erreur axios entiere : sa config contient le header Authorization.
        console.warn('[PlanBT] synchronisation des compteurs du minigrid echouee:', e?.response?.status, e?.message);
      });
  }, [planBTId, canSyncMeters, getAllNodesQuery, allLinkQuery]);
  
  
  const { saveChanges, isLoading: isSavingChanges, errors: saveErrors, clearErrors, saveProgress } = useGridEditorForPlanBT(planBTId);

  // === STATE MANAGEMENT ===
  
  // Grid data state
  const [elements, setElements] = useState<any[]>([]);
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedElement, setSelectedElement] = useState<any>(null);
  const [selectedConnection, setSelectedConnection] = useState<any>(null);
  
  // Tool and interaction state
  const [selectedTool, setSelectedTool] = useState<string>('select');
  
  // Canvas view state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  
  // Dragging state for canvas and elements
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isElementDragging, setIsElementDragging] = useState(false);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  
  // Connection creation state
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  
  // Map background and display settings
  const [mapBackground, setMapBackground] = useState<'none' | 'satellite' | 'terrain' | 'street' | 'osm' | 'custom'>('osm');
  const [mapOpacity, setMapOpacity] = useState(0.6);
  const [showGrid, setShowGrid] = useState(true);
  const [customMapUrl, setCustomMapUrl] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 48.8700, lng: 2.3000 });
  
  // GeoJSON layer management
  const [geoJSONLayers, setGeoJSONLayers] = useState<GeoJSONLayer[]>([]);
  
  // Canvas dimensions (fixed to prevent coordinate distortion)
  const [svgDimensions, setSvgDimensions] = useState({ width: 1200, height: 800 });
  
  // Store GPS bounds for coordinate conversion - initialize with default Paris bounds
  const [globalGpsBounds, setGlobalGpsBounds] = useState<GpsBounds>({
    minLat: 48.8,
    maxLat: 48.9,
    minLng: 2.2,
    maxLng: 2.4
  });
  
  // Edit/visualisation mode
  const [isEditMode, setIsEditMode] = useState(false);

  // Sortie forcée du mode édition seulement si le droit est réellement retiré
  // (rôle rétrogradé), pas sur une erreur transitoire de /me/app-role.
  useEffect(() => {
    if (editRightsRevoked) setIsEditMode(false);
  }, [editRightsRevoked]);

  // Layer visibility toggles
  const [layerVisibility, setLayerVisibilityState] = useState<LayerVisibility>({
    poles: true,
    meters: true,
    transformers: true,
    substations: true
  });

  // Live bubble metrics visibility toggles
  const [metricsVisibility, setMetricsVisibilityState] = useState<MetricsVisibility>({
    voltagePower: true,
    rssi: true,
    statusRing: true,
  });

  // Search query
  const [searchQuery, setSearchQuery] = useState('');

  // Save operation state
  const [lastSaveResult, setLastSaveResult] = useState<any>(null);
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null);

  // === CONFIGURATION ===
  
  // Available light types for pole elements
  const lightTypes = [
    { id: 'street-light', label: 'Street Light', power: 50 },
    { id: 'flood-light', label: 'Flood Light', power: 150 },
    { id: 'decorative', label: 'Decorative', power: 25 }
  ];

  // === DATA MANAGEMENT ===
  const { isLoadingNodes, isLoadingLinks, isLoadingStreetLights } = useDataManagement({
    getAllNodesQuery,
    allLinkQuery,
    getAllStreetLightsQuery,
    setElements,
    setConnections,
    setGlobalGpsBounds,
    setZoom,
    setPan,
    setMapCenter
  });

  // === AUTO-SELECT METER FROM URL ===
  const meterParam = searchParams.get('meter')
  useEffect(() => {
    if (!meterParam || elements.length === 0) return
    const meterElement = elements.find(
      (el: GridElement) => el.type === 'meter' && el.number === meterParam
    )
    if (meterElement && !selectedElement) {
      setSelectedElement(meterElement)
    }
  }, [meterParam, elements]) // eslint-disable-line react-hooks/exhaustive-deps

  // === EVENT HANDLERS ===
  const eventHandlers = useEventHandlers({
    svgRef,
    selectedTool,
    elements,
    connections,
    zoom,
    pan,
    isDragging,
    isElementDragging,
    draggedElement,
    dragStart,
    connectionStart,
    globalGpsBounds,
    setElements,
    setConnections,
    setSelectedElement,
    setIsDragging,
    setIsElementDragging,
    setDraggedElement,
    setDragStart,
    setConnectionStart,
    setPan,
    setZoom
  });

  // === ELEMENT MANAGEMENT ===
  const elementManagement = useElementManagement({
    elements,
    connections,
    selectedElement,
    selectedConnection,
    setElements,
    setConnections,
    setSelectedElement,
    setSelectedConnection
  });

  // === GEOJSON LAYER MANAGEMENT ===
  
  /**
   * Adds a new GeoJSON layer and fits the map to show it
   */
  const handleLayerAdd = (layer: GeoJSONLayer) => {
    setGeoJSONLayers(prev => [...prev, layer]);
    // Note: fitMapToBounds logic would need to be extracted to utils
  };

  /**
   * Removes a GeoJSON layer by ID
   */
  const handleLayerRemove = (layerId: string) => {
    setGeoJSONLayers(prev => prev.filter(layer => layer.id !== layerId));
  };

  /**
   * Updates properties of an existing GeoJSON layer
   */
  const handleLayerUpdate = (layerId: string, updates: Partial<GeoJSONLayer>) => {
    setGeoJSONLayers(prev => prev.map(layer => 
      layer.id === layerId ? { ...layer, ...updates } : layer
    ));
  };

  /**
   * Adds multiple elements at once (typically from GeoJSON import)
   */
  const handleElementsAdd = (newElements: any[]) => {
    setElements(prev => [...prev, ...newElements]);
  };

  /**
   * Merges elements with the same GPS coordinates to avoid stacked poles
   */
  const handleMergeElements = () => {
    setElements(prev => {
      const mergedElements = mergeElementsWithSameCoordinates(prev);
      if (mergedElements.length !== prev.length) {
        console.log(`🔄 Fusion des éléments: ${prev.length} → ${mergedElements.length} éléments`);
        alert(`✅ Fusion terminée !\n\n${prev.length - mergedElements.length} éléments ont été fusionnés.\n\nTotal: ${mergedElements.length} éléments.`);
      } else {
        alert('ℹ️ Aucun élément à fusionner trouvé.');
      }
      return mergedElements;
    });
  };

  /**
   * Adds multiple connections at once (typically from GeoJSON LineString import)
   */
  const handleConnectionsAdd = (newConnections: any[]) => {
    console.log('🔗 handleConnectionsAdd called with:', newConnections.length, 'connections');
    console.log('🔗 Current connections count:', connections.length);
    console.log('🔗 New connections:', newConnections);
    
    setConnections(prev => {
      const updated = [...prev, ...newConnections];
      console.log('🔗 Updated connections count:', updated.length);
      return updated;
    });
  };

  // === UTILITY FUNCTIONS ===
  
  /**
   * Calculates the current geographic bounds visible in the map
   */
  const getMapBounds = () => {
    return calculateMapBounds(zoom, mapCenter);
  };

  /**
   * Fits the map view to show the specified geographic bounds
   */
  const fitMapToBounds = (bounds: GpsBounds) => {
    const settings = calculateFitMapSettings(bounds);
    setMapCenter(settings.mapCenter);
    setZoom(settings.zoom);
    setPan(settings.pan);
  };

  /**
   * Fits the map view to show all elements with optimal centering and zoom
   */
  const fitMapToElements = () => {
    const bounds = calculateGpsBounds(elements);
    if (bounds) {
      const settings = calculateFitMapSettings(bounds, elements);
      setMapCenter(settings.mapCenter);
      setZoom(settings.zoom);
      setPan(settings.pan);
    }
  };

  /**
   * Resets the view to default zoom and pan settings
   */
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  /**
   * Handles tool selection and clears related state
   */
  const handleToolSelect = (tool: string) => {
    setSelectedTool(tool);
    setConnectionStart(null);
  };

  /**
   * Counts pending changes (elements, connections, street lights)
   */
  const countPendingChanges = useCallback(() => {
    const isPending = (item: { action?: ElementAction }) =>
      item.action === ElementAction.TO_CREATE ||
      item.action === ElementAction.TO_UPDATE ||
      item.action === ElementAction.TO_DELETE;
    const allLights = elements.flatMap(el => el.lights || []);
    return (
      elements.filter(isPending).length +
      connections.filter(isPending).length +
      allLights.filter(isPending).length
    );
  }, [elements, connections]);

  /**
   * Checks if there are any pending changes to save
   */
  const hasChanges = useCallback(() => countPendingChanges() > 0, [countPendingChanges]);

  /**
   * Saves the current grid state to the API
   */
  const saveChangesHandler = async () => {
    if (!canEditPlan) return;
    // Group elements by action for easier processing
    const elementsTO_CREATE = elements.filter(el => el.action === ElementAction.TO_CREATE);
    const elementsTO_UPDATE = elements.filter(el => el.action === ElementAction.TO_UPDATE);
    const elementsTO_DELETE = elements.filter(el => el.action === ElementAction.TO_DELETE);
    
    // Group connections by action for easier processing (use full connections list including deleted ones)
    const connectionsTO_CREATE = connections.filter(conn => conn.action === ElementAction.TO_CREATE);
    const connectionsTO_UPDATE = connections.filter(conn => conn.action === ElementAction.TO_UPDATE);
    const connectionsTO_DELETE = connections.filter(conn => conn.action === ElementAction.TO_DELETE);
    
    // Group street lights by action - collect all lights from all elements
    const allLights = elements.flatMap(el => el.lights || []);
    const lightsTO_CREATE = allLights.filter(light => light.action === ElementAction.TO_CREATE);
    const lightsTO_UPDATE = allLights.filter(light => light.action === ElementAction.TO_UPDATE);
    const lightsTO_DELETE = allLights.filter(light => light.action === ElementAction.TO_DELETE);
    
    console.log('💡 Street lights summary:', {
      total: allLights.length,
      toCreate: lightsTO_CREATE.length,
      toUpdate: lightsTO_UPDATE.length,
      toDelete: lightsTO_DELETE.length,
      lightsToCreate: lightsTO_CREATE,
      lightsToUpdate: lightsTO_UPDATE,
      lightsToDelete: lightsTO_DELETE
    });
    
    // Log complet du résumé des changements
    console.log('📋 RÉSUMÉ COMPLET DES CHANGEMENTS À SAUVEGARDER:', {
      '🏗️ ÉLÉMENTS': {
        'Nouveaux': elementsTO_CREATE.length,
        'Modifiés': elementsTO_UPDATE.length,
        'Supprimés': elementsTO_DELETE.length,
        'Détails nouveaux': elementsTO_CREATE.map(el => ({ id: el.id, name: el.name, type: el.type })),
        'Détails modifiés': elementsTO_UPDATE.map(el => ({ id: el.id, name: el.name, type: el.type })),
        'Détails supprimés': elementsTO_DELETE.map(el => ({ id: el.id, name: el.name, type: el.type }))
      },
      '🔗 CONNEXIONS': {
        'Nouvelles': connectionsTO_CREATE.length,
        'Modifiées': connectionsTO_UPDATE.length,
        'Supprimées': connectionsTO_DELETE.length,
        'Détails nouvelles': connectionsTO_CREATE.map(conn => ({ id: conn.id, from: conn.fromId, to: conn.toId, type: conn.type })),
        'Détails modifiées': connectionsTO_UPDATE.map(conn => ({ id: conn.id, from: conn.fromId, to: conn.toId, type: conn.type })),
        'Détails supprimées': connectionsTO_DELETE.map(conn => ({ id: conn.id, from: conn.fromId, to: conn.toId, type: conn.type }))
      },
      '💡 STREET LIGHTS': {
        'Nouvelles': lightsTO_CREATE.length,
        'Modifiées': lightsTO_UPDATE.length,
        'Supprimées': lightsTO_DELETE.length,
        'Détails nouvelles': lightsTO_CREATE.map(light => ({ id: light.id, name: light.name, poleId: light.poleId, zhagaDevui: light.zhagaDevui })),
        'Détails modifiées': lightsTO_UPDATE.map(light => ({ id: light.id, name: light.name, poleId: light.poleId, zhagaDevui: light.zhagaDevui })),
        'Détails supprimées': lightsTO_DELETE.map(light => ({ id: light.id, name: light.name, poleId: light.poleId, zhagaDevui: light.zhagaDevui }))
      },
      '📊 TOTAUX': {
        'Total éléments': elementsTO_CREATE.length + elementsTO_UPDATE.length + elementsTO_DELETE.length,
        'Total connexions': connectionsTO_CREATE.length + connectionsTO_UPDATE.length + connectionsTO_DELETE.length,
        'Total street lights': lightsTO_CREATE.length + lightsTO_UPDATE.length + lightsTO_DELETE.length,
        'Total changements': elementsTO_CREATE.length + elementsTO_UPDATE.length + elementsTO_DELETE.length + 
                           connectionsTO_CREATE.length + connectionsTO_UPDATE.length + connectionsTO_DELETE.length +
                           lightsTO_CREATE.length + lightsTO_UPDATE.length + lightsTO_DELETE.length
      }
    });
    
    // Summary of changes for implementation
    const changesSummary: ChangesSummaryPlanBT = {
      elements: {
        create: elementsTO_CREATE,
        update: elementsTO_UPDATE,
        delete: elementsTO_DELETE,
      },
      connections: {
        create: connectionsTO_CREATE,
        update: connectionsTO_UPDATE,
        delete: connectionsTO_DELETE
      },
      streetLights: {
        create: lightsTO_CREATE,
        update: lightsTO_UPDATE,
        delete: lightsTO_DELETE
      }
    };
    
    try {
      const result = await saveChanges(changesSummary);
      setLastSaveResult(result);
      console.log('💾 Save changes result:', result);
      
      // Si la sauvegarde est réussie, réinitialiser les actions des éléments
      if (result.success) {
        console.log('✅ Save successful, resetting element actions');
        const nodeIdMap = result.nodeIdMap ?? {};
        
        // Réinitialiser seulement les éléments qui ne sont pas supprimés,
        // et mettre à jour les IDs temporaires (frontend) vers les IDs Couchbase réels.
        setElements(prev => prev.map(el => {
          if (el.action === ElementAction.TO_DELETE) {
            return null;
          }
          const updatedLights = el.lights?.map((light: Light) => {
            if (light.action === ElementAction.TO_DELETE) {
              return null;
            }
            return { ...light, action: ElementAction.UN_CHANGED };
          }).filter(Boolean) as Light[] || [];

          // Remplacer l'ID temporaire par l'ID Couchbase si disponible
          const realId = nodeIdMap[el.id] ?? el.id;
          return { ...el, id: realId, action: ElementAction.UN_CHANGED, lights: updatedLights };
        }).filter(Boolean) as GridElement[]);
        
        // Réinitialiser seulement les connexions qui ne sont pas supprimées
        setConnections(prev => prev.map(conn => {
          if (conn.action === ElementAction.TO_DELETE) {
            return null;
          }
          // Mettre à jour les références fromId/toId si elles pointaient vers des IDs temporaires
          const realFromId = nodeIdMap[conn.fromId] ?? conn.fromId;
          const realToId = nodeIdMap[conn.toId] ?? conn.toId;
          return { ...conn, fromId: realFromId, toId: realToId, action: ElementAction.UN_CHANGED };
        }).filter(Boolean) as Connection[]);

        // Mettre à jour l'élément sélectionné si son ID a changé
        if (selectedElement && nodeIdMap[selectedElement.id]) {
          setSelectedElement((prev: GridElement | null) => prev ? { ...prev, id: nodeIdMap[prev.id] ?? prev.id } : null);
        }

        // Rafraîchir le cache React Query pour synchroniser avec le backend
        getAllNodesQuery.refetch();
      }
    } catch (error) {
      console.error('💾 Save changes error:', (error as any)?.response?.status, (error as any)?.message);
      setLastSaveResult({
        success: false,
        message: 'Erreur lors de la sauvegarde',
        errors: [error]
      });
      
      // Effacer automatiquement les erreurs après 3 secondes
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        console.log('⏰ Auto-clearing errors after 3 seconds');
        setLastSaveResult(null);
        clearErrors();
      }, 3000);
    }
  };

  /**
   * Clears save errors from the state
   */
  const clearSaveErrorsHandler = () => {
    // Annuler le timer automatique s'il existe
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    clearErrors();
    setLastSaveResult(null);
  };

  // === EFFECTS ===
  
  /**
   * Clear success status when new changes are detected
   */
  useEffect(() => {
    const hasCurrentChanges = hasChanges();
    
    // If there are new changes and the last save was successful, clear the success status
    if (hasCurrentChanges && lastSaveResult?.success) {
      console.log('🔄 Clearing success status due to new changes');
      setLastSaveResult(null);
    }
  }, [elements, connections, hasChanges, lastSaveResult?.success]);

  /**
   * Auto-clear errors from hook after 3 seconds
   */
  useEffect(() => {
    if (saveErrors.length > 0 && !lastSaveResult?.success) {
      // Démarrer le timer automatique pour effacer les erreurs
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        console.log('⏰ Auto-clearing hook errors after 3 seconds');
        setLastSaveResult(null);
        clearErrors();
      }, 3000);
    }
  }, [saveErrors.length, lastSaveResult?.success, clearErrors]);

  /**
   * Cleanup timer on component unmount
   */
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, []);

  // Garde beforeunload : prévient la perte de modifications non enregistrées
  // (notamment quand le droit d'édition a été retiré en cours de session).
  const pendingChangesCount = countPendingChanges();
  useEffect(() => {
    if (pendingChangesCount === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chaîne non vide : requise par les navigateurs antérieurs à Chrome 119.
      e.returnValue = 'unsaved';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [pendingChangesCount]);

  // === RETURN OBJECTS ===
  
  // Filter out deleted elements and connections from display (but keep them for save operations)
  // Also filter by layer visibility and search query
  const visibleElements = elements.filter(el => {
    if (el.action === ElementAction.TO_DELETE) return false;
    // Layer visibility filter
    if (el.type === 'pole' && !layerVisibility.poles) return false;
    if (el.type === 'meter' && !layerVisibility.meters) return false;
    if (el.type === 'transformer' && !layerVisibility.transformers) return false;
    if (el.type === 'power-plant' && !layerVisibility.substations) return false;
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = el.name?.toLowerCase().includes(q);
      const matchesNumber = el.number?.toLowerCase().includes(q);
      const matchesType = el.type.toLowerCase().includes(q);
      if (!matchesName && !matchesNumber && !matchesType) return false;
    }
    return true;
  });
  const visibleConnections = connections.filter(conn => conn.action !== ElementAction.TO_DELETE);

  // Aggregate all state into a single object for the component
  const state: GridEditorState = {
    elements: visibleElements,
    connections: visibleConnections,
    selectedElement,
    selectedConnection,
    selectedTool,
    zoom,
    pan,
    isDragging,
    dragStart,
    isElementDragging,
    draggedElement,
    connectionStart,
    mapBackground,
    mapOpacity,
    showGrid,
    customMapUrl,
    mapCenter,
    geoJSONLayers,
    svgDimensions,
    isLoadingNodes,
    isLoadingLinks,
    isLoadingStreetLights,
    isSavingChanges,
    saveErrors,
    lastSaveResult,
    saveProgress,
    globalGpsBounds,
    isEditMode,
    unsavedChangesBlockedCount: editRightsRevoked ? pendingChangesCount : 0,
    layerVisibility,
    metricsVisibility,
    searchQuery
  };

  // Aggregate all actions into a single object for the component
  const actions: GridEditorActions = {
    // Tool management
    setSelectedTool: handleToolSelect,
    
    // View controls
    setZoom,
    setPan,
    resetView,
    
    // Element management
    addElement: (x: number, y: number) => eventHandlers.handleSVGClick({ clientX: x, clientY: y, preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent<SVGSVGElement>),
    deleteElement: elementManagement.deleteElement,
    deleteConnection: elementManagement.deleteConnection,
    updateElement: elementManagement.updateElement,
    updateConnection: elementManagement.updateConnection,
    setSelectedElement,
    setSelectedConnection,
    setConnectionStart,
    
    // Event handlers
    handleMouseDown: eventHandlers.handleMouseDown,
    handleMouseMove: eventHandlers.handleMouseMove,
    handleMouseUp: eventHandlers.handleMouseUp,
    handleElementMouseDown: eventHandlers.handleElementMouseDown,
    handleSVGClick: eventHandlers.handleSVGClick,
    handleWheel: eventHandlers.handleWheel,
    
    // Map controls
    setMapBackground,
    setMapOpacity,
    setShowGrid,
    setCustomMapUrl,
    setMapCenter,
    
    // GeoJSON layer management
    handleLayerAdd,
    handleLayerRemove,
    handleLayerUpdate,
    handleElementsAdd,
    handleConnectionsAdd,
    
    // Light management
    addLight: (elementId: string, lightType: string) => elementManagement.addLight(elementId, lightType, lightTypes),
    removeLight: elementManagement.removeLight,
    updateLight: elementManagement.updateLight,
    
    // Utility functions
    getMapBounds,
    fitMapToBounds,
    fitMapToElements,
    setSvgDimensions,
    
    // Save functionality
    saveChanges: saveChangesHandler,
    clearSaveErrors: clearSaveErrorsHandler,
    
    // Element merging
    mergeElements: handleMergeElements,
    
    // Change detection
    hasChanges: hasChanges(),

    // Edit mode toggle
    toggleEditMode: () => setIsEditMode(prev => (canEditPlan ? !prev : false)),

    // Layer visibility
    setLayerVisibility: (layer: keyof LayerVisibility, visible: boolean) => {
      setLayerVisibilityState(prev => ({ ...prev, [layer]: visible }));
    },

    // Metrics bubble visibility
    setMetricsVisibility: (metric: keyof MetricsVisibility, visible: boolean) => {
      setMetricsVisibilityState(prev => ({ ...prev, [metric]: visible }));
    },

    // Search
    setSearchQuery,

  };

  // Return the complete presenter interface
  return {
    state,
    actions,
    lightTypes
  };
};
